import styles from '../../styles/DescriptionStyle.module.css'
import {SaveDto } from "../../api";
import React, {useContext } from "react";
import {ConfiguratorContext} from "../../context";
import {useIntl} from "react-intl";
import {useQuoteContext} from "../../contexts/QuoteContext";
import useQuote from "../../swr/useQuote";
import useLeadTime from "../../swr/useLeadTime";
import Utils from "../../util/util";
import {
    Permission,
    Quote,
    SalesTeam
} from "../../api/models";
import ModelSelectionWizard, {SelectedModelInfo} from "./ModelSelectionWizard";
import {DescriptionsItemType} from "antd/lib/descriptions";
import dayjs, {Dayjs} from "dayjs";
import Paragraph from "antd/lib/typography/Paragraph";
import QuoteTrucksButtonModal from "./QuoteTrucksButtonModal";
import PoNumberEditor from "./PoNumberEditor";
import CustomerEntry, {CustomerAddressBlock} from "../customer_entry";
import ShippingDestinationModal, {formatShippingDestination} from "../ShippingDestinationModal";
import QuoteOwnerAssignment from "./OwnerAssignment";
import {SalesTeamRole, SelectSalesTeamButtonModal} from "../SelectSalesTeamButton";
import {
    Card,
    DatePicker,
    Descriptions, DescriptionsProps,
    notification,
    Skeleton
} from "antd";
import QuoteSalesRequestInput from "./QuoteSalesRequestInput";
import QuoteNotesInput from "./QuoteNotesInput";
import ProductionDateInfo from "./ProductionDateInfo";
import QuoteQuantityInput from "./QuoteQuantityInput";
import {FEATURE_DOCUSIGN} from "../../api/features";
import CustomerShippingInfo, {RequestedShipping} from "./CustomerShippingInfo";
import OrderProbability from "./OrderProbability";
import StateSelectionPopover from "./StateSelectionPopover";

const QuoteDetailsContents = (props: {
    onQuoteChange?: (n?: SaveDto | undefined) => void
    onChange?: (q:Quote | undefined) => void
    style?: React.CSSProperties
}) => {

    const configurator = useContext(ConfiguratorContext);
    const intl = useIntl();

    const {quote} = useQuoteContext();
    const quoteAsync = useQuote({quoteId: quote?.quoteId, revision: quote?.revision});
    const leadTimeAsync = useLeadTime({quoteId: quote?.id});

    const {
        isOrder,
        isReadOnly,
        readOnlyMsg,
        canChangeReadOnly,
    } = Utils.getQuoteState(configurator, quote);

    const isOwner = quote?.owner?.id === configurator.userInfo?.id;
    const isDealer = configurator.isDealerSales();
    const isAdminOrEngineering = configurator.isAdmin() || configurator.isEngineering();
    const isAdmin = configurator.isAdmin();
    const isFinance = configurator.isFinance();
    const isSalesDesk = configurator.isSalesDesk();
    const isEngineering = configurator.isEngineering();
    const isProduction = configurator.isProduction()
    const isDocusignEnabled = configurator.hasFeature(FEATURE_DOCUSIGN);
    const isQuoteSales = quote?.salesTeam?.sales?.some(rep => rep.id === configurator.userInfo?.id);

    const updateRequestedShipping = async (quoteRevisionId: number | undefined, requestedShipping: RequestedShipping | undefined): Promise<Quote | undefined> => {
        if (!quoteRevisionId) return;
        if (!requestedShipping) return;

        try {
            const resp = await configurator.api.updateRequestedShipping(quoteRevisionId, requestedShipping);

            notification.success({message: "Requested shipping updated"})

            return resp.data;
        } catch (e: any) {
            const errorMsg = intl.formatMessage({ id: e.message || e.response?.data.message });
            const msg =  "Failed to update requested shipping. " + errorMsg

            notification.error( { message: msg });
        }

        return;
    }

    const handleChangeRequestedShipping = async (requestedShipping: RequestedShipping | undefined) => {
        await updateRequestedShipping(quote?.displayRevisionId, requestedShipping);
        props.onQuoteChange?.()
    }

    const handleModelChange = async (selectedModelInfo: SelectedModelInfo | undefined)  => {
        if (!selectedModelInfo) return;

        const saved = await saveModel( selectedModelInfo );
        if ( saved ) {
            props.onChange?.(saved);
        }
    }

    const saveModel = async (selectedModel:SelectedModelInfo) : Promise<Quote | undefined> => {
        const quoteRevisionId = quote?.displayRevisionId;
        if ( !quoteRevisionId ) return;
        if ( !selectedModel.modelInfo?.id ) return;

        const selections = Object.values(selectedModel.selections || {}).flat();
        try {
            const resp = await configurator.api.saveModel(quoteRevisionId, selectedModel.modelInfo.id, selections );

            return resp.data;
        } catch (e:any) {
            const errorMsg = intl.formatMessage({ id: e.message });
            console.log( "Failed to save model. " + errorMsg );
        }

        return;
    }


    const isWithinLeadTimeCheck = (leadTime: number | undefined): boolean => {
        if (isAdminOrEngineering || isSalesDesk || configurator.isProduction()) {
            return false;
        }
        return Utils.isWithinLeadTime(quote?.productionDate, leadTime);
    }

    const getDefaultDisabledMsg = () => {
        return quote?.archived ? "Archived quote cannot be modified."
            : isReadOnly ? readOnlyMsg
            : isReadOnly ? readOnlyMsg
                //          : isWorking ? "Please wait till background work has completed."
                : undefined;
    }

    const getSelectModelDisabledMsg = () => {
        return ( isOrder && !( isAdmin || isEngineering) ) ? "The model cannot be changed on an order."
            : getDefaultDisabledMsg();
    }

    const quoteItems:DescriptionsItemType[] = [];

    quoteItems.push( {
            label: "Model",
            children:
                <ModelSelectionWizard
                    value={{
                        modelInfo: quote?.model,
                        modelYear: quote?.modelYear || ( dayjs().year() + 1 ),
                        selections: quote?.selections
                    }}
                    onChange={handleModelChange}
                    disabled={!!getSelectModelDisabledMsg()}
                    onDisabledClick={() => Utils.notifyDisabled(getSelectModelDisabledMsg())} />
        }
    );

    if ( quote?.partNumberString ) {
        quoteItems.push( {
            label: "Part No",
            children:
                <div style={{display: "flex", gap: "0.5rem"}}>
                    <span data-testid="partNumberString" >{quote.partNumberString || "NA"}</span>
                    {quote.partNumberString.trim().length && <Paragraph copyable={{text: quote.partNumberString}} />}
                </div>
        })
    }

    if ( quote?.serialNumberStr ) {
        quoteItems.push( {
            label: "Serial Numbers",
            children:
                <QuoteTrucksButtonModal />
        })
    }

    quoteItems.push({
        label: "Quantity",
        children:
            <QuoteQuantityInput quote={quote} readOnly={isReadOnly} onChange={(quantity: number) => props.onQuoteChange?.({ quantity })  } />
    })

    if( !isDealer && !!quote?.salesOrderNumber ) {
        quoteItems.push({
            key: "salesOrderNumber",
            label: quote.salesOrderNumber && !isDealer ? "Epicor Sales Order" : "",
            children: quote.salesOrderNumber && !isDealer ? <span>{quote.salesOrderNumber}</span> : <></>
        })
    }

    if (!isDocusignEnabled || (isSalesDesk || isAdmin)) {
        quoteItems.push({
            label: "PO Number",
            children:
                <PoNumberEditor
                    disabled={!(isOwner || isSalesDesk || isAdmin || isFinance || isQuoteSales )}
                    value={quote?.poNumber}
                    onChange={props.onChange}
                />
        })
    }

    if( quote?.dealerCustomer ) {
        const dealerCustomer = quote.dealerCustomer;
        quoteItems.push({
            label: "Dealer",
            children:
                <div>
                    <div>
                        <div>{quote.owner?.dealerName}</div>
                        {!!dealerCustomer?.addressLine1 && <div>{dealerCustomer.addressLine1}</div>}
                        {!!dealerCustomer?.addressLine2 && <div>{dealerCustomer.addressLine2}</div>}
                        <div>{[[dealerCustomer?.city, dealerCustomer?.stateProvince].filter(v=>v).join(", "), dealerCustomer?.postalCode].filter(v=>v).join(" ")}</div>
                    </div>
                    <div>
                        {!!dealerCustomer?.contactName && <div>{[dealerCustomer.contactName, dealerCustomer.contactRole && ["(", dealerCustomer.contactRole, ")"].join(" ") ].filter(v=>v).join( " " )}</div>}
                        {!!dealerCustomer?.contactPhone && <div>{dealerCustomer.contactPhone}</div>}
                        {!!dealerCustomer?.contactEmail && <div>{dealerCustomer.contactEmail}</div>}
                    </div>
                </div>
        })
    }

    quoteItems.push({
        label: "End Customer",
        children:
            <CustomerEntry
                style={{
                    display: "inline-block",
                    alignItems: "baseline",
                    textAlign: "left",
                    whiteSpace: "break-spaces",
                }}
                value={quote?.endCustomer}
                onChange={(c) => props.onQuoteChange?.({endCustomerId: c?.id ?? null })}
                disabled={isReadOnly}
            >
                <div style={{textDecoration: !isReadOnly ? "underline" : undefined, color: "black", display: "inline-block" }}>
                    {( quote?.endCustomer )
                        ? <CustomerAddressBlock customer={quote.endCustomer} />
                        : isReadOnly ? "None"
                        : "Add Customer"}
                </div>
            </CustomerEntry>
    })

    quoteItems.push({
        label: "Destination",
        children:
            <ShippingDestinationModal
                style={{
                    display: "inline-block",
                    alignItems: "baseline",
                    textAlign: "left",
                    whiteSpace: "break-spaces",
                }}
                value={quote?.shippingDestination}
                onChange={(sd) => props.onQuoteChange?.({shippingDestination: sd?.id ?? null })}
                disabled={isWithinLeadTimeCheck(leadTimeAsync.data?.shippingDestinationLeadTime) || isReadOnly}
            >
                <div style={{textDecoration: !isReadOnly ? "underline" : undefined, color: "black" }}>
                    {( quote?.shippingDestination ) ? formatShippingDestination(quote.shippingDestination)
                        : isReadOnly ? "None"
                            : "Add Shipping Destination"}
                </div>
            </ShippingDestinationModal>
    })

    quoteItems.push({
        span: 5,
        label: "State Registration",
        children:
            <StateSelectionPopover
                                   readOnly={isReadOnly}
                                   onChange={(r) => props.onQuoteChange?.({stateRegistration: r ?? null })} />
    })

    const scheduleItems:DescriptionsItemType[] = [];

    if( quote?.productionDate ) {
        scheduleItems.push({
            label: "Production Date",
            children:
                <QuoteTrucksButtonModal >
                    <ProductionDateInfo productionDate={quote?.productionDate} leadTime={leadTimeAsync.data} />
                </QuoteTrucksButtonModal>

        })
    }

    if( quote?.shippingDate ) {
        scheduleItems.push({
            label: "Ship Date",
            children:
                <QuoteTrucksButtonModal >
                    {quote?.shippingDate ? dayjs(quote?.shippingDate).format('M/D/YYYY') : "Not Available"}
                </QuoteTrucksButtonModal>
        })
    }

    scheduleItems.push({
        label: "Requested Ship Date",
        children:
            <CustomerShippingInfo
                value={{
                    customerShippingDate: quote?.customerShippingDate ? dayjs(quote.customerShippingDate) : undefined,
                    cadence: quote?.cadence,
                    truckCustomerShippingDateList: quote?.truckCustomerShippingDateList,
                }}
                disabled={isReadOnly && !( canChangeReadOnly || isProduction )}
                onChange={handleChangeRequestedShipping} />
    })


    const showSalesRequests = !Object.values(quote?.selectedCustomOptions || {})?.length && !!quote?.salesRequests?.trim().length;
    if(showSalesRequests) {
        quoteItems.push({
            label: "Sales Requests",
            children:
                <QuoteSalesRequestInput quote={quote} onChange={(n: string | undefined) => props.onQuoteChange?.({ notes: n ?? null })  } />
        })
    }

    const forecastItems:DescriptionsItemType[] = [];

    forecastItems.push({
        label: "Order Probability",
        children:
            <OrderProbability value={quote?.orderProbability} onChange={(n: string | undefined) => props.onQuoteChange?.({ orderProbability: n ?? null })  } />
    })

    forecastItems.push({
        label: "Order Date",
        children:
            <DatePicker
                allowClear={true}
                data-testid="request-date-picker"
                value={quote?.expectedOrderDate ? dayjs(quote.expectedOrderDate) : undefined} onChange={(dt:Dayjs | undefined) => props.onQuoteChange?.({ expectedOrderDate: dt?.toDate() ?? null })  }
            />
    })

    forecastItems.push({
        label: "Ship Date",
        children:
            <DatePicker
                allowClear={true}
                data-testid="request-date-picker"
                value={quote?.expectedShipDate ? dayjs(quote.expectedShipDate) : undefined} onChange={(dt: Dayjs | undefined) => props.onQuoteChange?.({ expectedShipDate: dt?.toDate() ?? null })  }
            />
    })

    const loading = quoteAsync?.isLoading;
    return <>
        <Skeleton active loading={loading} >

            <div style={{display: "flex", flexWrap: "wrap", gap: "1rem"}} >

                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }} >

                    <Descriptions className={[styles['details-description'], styles['vertical']].join(" ")}
                                  column={3}
                                  items={quoteItems}
                                  layout="vertical" size="small" colon={false}
                                  style={{padding: 0}} />

                    <Descriptions className={styles['details-description']}
                                  items={scheduleItems}
                                  layout="vertical" size="small" colon={false}
                                  style={{padding: 0}}
                    />

                    <Card title={"Notes"} size={"small"} >
                        {/* removed readOnly check for now that Aaron added on 4/29, may need to re-add later */}
                        <QuoteNotesInput quote={quote} readOnly={false} onChange={(n: string | undefined) => props.onQuoteChange?.({ notes: n ?? null })  } />
                    </Card>

                </div>


                    <Card title={"Sales Team"} size={"small"} style={{maxWidth: "30rem"}} >
                        <SalesTeamDescription
                            onQuoteChange={props.onQuoteChange}
                            onChange={props.onChange}
                            layout="vertical" size="small" colon={false}
                            style={{padding: 0}}
                        />
                    </Card>

                {(!isDealer && ( isSalesDesk || isQuoteSales ) ) &&
                    <Card title={"Forecast"} size={"small"} style={{maxWidth: "10rem"}} >
                        <Descriptions className={styles['details-description']}
                                      column={1}
                                      items={forecastItems}
                                      layout="vertical" size="small" colon={false}
                                      style={{padding: 0}}
                        />
                    </Card>}

            </div>
        </Skeleton>
    </>

}

const SalesTeamDescription = (props: DescriptionsProps & {
    onChange?: (q?: Quote | undefined) => void
    onQuoteChange?: (n?: SaveDto | undefined) => void
}) => {

    const configurator = useContext(ConfiguratorContext);
    const {quote} = useQuoteContext();
    const { onChange:a, onQuoteChange, ...descProps } = props;

    const {
        isSubordinate,
    } = Utils.getQuoteState(configurator, quote);

    const isAdmin = configurator.isAdmin();
    const isSalesDesk = configurator.isSalesDesk();
    const isQuoteOwner = quote?.owner?.id === configurator.userInfo?.id;
    const hasDealerWritePermission = configurator.hasAnyPermission([Permission.DEALER_ADMIN_WRITE, Permission.DEALER_MANAGEMENT_WRITE]);

    const canReassignQuoteOwner =  isQuoteOwner || isAdmin || isSalesDesk || hasDealerWritePermission || isSubordinate;

    const handleSalesTeamChange = (salesTeam:SalesTeam | undefined) => {

        const dto = salesTeam ? {
            id: salesTeam.id,
            name: salesTeam.name,
            sales: salesTeam.sales?.map( u => u.id ),
            engineers: salesTeam.engineers?.map( u => u.id ),
            support: salesTeam.support?.map( u => u.id ),
            viewers: salesTeam.viewers?.map( u => u.id ),
        } : null;

        props.onQuoteChange?.({ salesTeam: dto })
    }


    const salesTeamItems:DescriptionsItemType[] = [];

    if( !!quote?.salesTeam?.sales?.length  ) {
        salesTeamItems.push({
            label: !!quote?.owner?.dealerId ? "BM Representative(s)" : "Additional Sales",
            children: <SelectSalesTeamButtonModal
                style={{whiteSpace: "break-spaces", textAlign: "right"}}
                value={quote?.salesTeam}
                onChange={handleSalesTeamChange}
                role={SalesTeamRole.Sales}
                filter={{
                    dealerId: quote?.owner?.dealerId ,
                    isUserSalesTeam: quote?.owner?.dealerId ? undefined : true
                }} />
        })
    }

    salesTeamItems.push({
        label: "Application Engineers",
        children: <SelectSalesTeamButtonModal
            style={{whiteSpace: "break-spaces", textAlign: "right"}}
            value={quote?.salesTeam}
            onChange={handleSalesTeamChange}
            role={SalesTeamRole.Engineer}
            filter={{
                dealerId: quote?.owner?.dealerId ,
                isUserSalesTeam: quote?.owner?.dealerId ? undefined : true
            }} />
    })

    salesTeamItems.push({
        label: "Sales Support",
        children: <SelectSalesTeamButtonModal
            style={{whiteSpace: "break-spaces", textAlign: "right"}}
            value={quote?.salesTeam}
            onChange={handleSalesTeamChange}
            role={SalesTeamRole.Support}
            filter={{
                dealerId: quote?.owner?.dealerId ,
                isUserSalesTeam: quote?.owner?.dealerId ? undefined : true
            }} />
    })

    salesTeamItems.push({
        label: "Viewers",
        children: <SelectSalesTeamButtonModal
            style={{whiteSpace: "break-spaces", textAlign: "right"}}
            value={quote?.salesTeam}
            onChange={handleSalesTeamChange}
            role={SalesTeamRole.Viewer}
            filter={{
                dealerId: quote?.owner?.dealerId ,
                isUserSalesTeam: quote?.owner?.dealerId ? undefined : true
            }} />
    })

    return <>
        {( quote?.owner ) &&
          <Descriptions
            className={styles['details-description']}
            items={[
                {
                    label: "Salesperson",
                    children: <QuoteOwnerAssignment
                        disabled={!canReassignQuoteOwner}
                        value={quote?.owner}
                        onChange={props.onChange}
                    />
                }

            ] }
            layout="vertical" size="small" colon={false}
            style={{padding: 0}}
            {...descProps}
          />}

    <Descriptions
        className={styles['details-description']}
        items={salesTeamItems}
        layout="vertical" size="small" colon={false}
        style={{padding: 0}}
        {...descProps}
    />
    </>
}


export default QuoteDetailsContents;
